import { useCallback, useEffect, useState } from 'react';
import {
  EVENTS_EMIT,
  useSocketConversationChatTs
} from './useSocketConversationChatTs';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useSocketEvents } from './socket-utils';

/**
 * Monitora em tempo real quais atendentes estão logados no sistema
 * Também mostra atendentes que não estão logados em filas de chat
 */
export function useAgentsLoggedInChatQueues() {
  const [state, setState] = useState({
    response: { system: [], chat: [] }
  });
  const { socketChatTS } = useSocketConversationChatTs();

  const emitGetAgentsLoggedInSystem = useCallback(() => {
    socketChatTS.emit(EVENTS_EMIT.GET_AGENTS_LOGGED_IN_SYSTEM);
  }, [socketChatTS]);

  const emitGetAgentsLoggedInChatQueues = useCallback(() => {
    socketChatTS.emit(EVENTS_EMIT.GET_AGENTS_LOGGED_IN_CHAT_QUEUES);
  }, [socketChatTS]);

  useEffect(() => {
    emitGetAgentsLoggedInSystem();
  }, [emitGetAgentsLoggedInSystem]);

  useEffect(() => {
    emitGetAgentsLoggedInChatQueues();
  }, [emitGetAgentsLoggedInChatQueues]);

  /**
   * Lista de todos os atendentes que estão com a aba do Flex aberta, mesmo que não estejam em uma fila de chat
   */
  const handleUpdateSystem = useCallback((update) => {
    const { success, error, data } =
      UPDATE_AGENTS_LOGGED_IN_SYSTEM_SCHEMA.safeValidateSync(update);

    if (!success) {
      toast.error(
        error?.message ?? 'Não foi possível validar a resposta do servidor'
      );
      console.error(error);
      return;
    }

    setState((prev) => ({
      ...prev,
      response: { ...prev.response, system: data.data }
    }));
  }, []);

  /**
   * Lista de todos os atendentes que estão logados em uma fila do chat, mesmo que não estejam com o Flex aberto
   */
  const handleUpdateChatQueues = useCallback((update) => {
    const { success, error, data } =
      UPDATE_AGENTS_LOGGED_IN_CHAT_QUEUES_SCHEMA.safeValidateSync(update);

    if (!success) {
      toast.error(
        error?.message ?? 'Não foi possível validar a resposta do servidor'
      );
      console.error(error);
      return;
    }

    setState((prev) => {
      const updatedLogins = new Set(data.map((agent) => agent.login));
      const filteredChat = prev.response.chat.filter(
        (agt) => !updatedLogins.has(agt.login)
      );
      const newChat = filteredChat.concat(data);

      return {
        ...prev,
        response: { ...prev.response, chat: newChat }
      };
    });
  }, []);

  useSocketEvents(socketChatTS, {
    [EVENTS_EMIT.UPDATE_AGENTS_LOGGED_IN_SYSTEM]: handleUpdateSystem,
    [EVENTS_EMIT.UPDATE_AGENTS_LOGGED_IN_CHAT_QUEUES]: handleUpdateChatQueues
  });

  return {
    ...state,
    emitGetAgentsLoggedInChatQueues,
    emitGetAgentsLoggedInSystem
  };
}

const UPDATE_AGENTS_LOGGED_IN_SYSTEM_SCHEMA = yup
  .object({
    action: yup
      .string()
      .oneOf(
        ['login', 'logout', 'all_agents'],
        'Não foi possível verificar a ação a ser executada'
      )
      .required('Ação não informada recebida do servidor'),
    data: yup.array(
      yup.object({
        login: yup
          .string('Não foi possível validar o login do atendente')
          .defined('Não foi possível validar o login do atendente'),
        pauseId: yup.number().nullable(),
        name: yup
          .string()
          .defined('Não foi possível validar o nome do atendente')
      })
    )
  })
  .typeError('O formato dos dados recebidos do servidor é inválido.');

const UPDATE_AGENTS_LOGGED_IN_CHAT_QUEUES_SCHEMA = yup.array(
  yup.object({
    login: yup
      .string('Não foi possível validar o login do atendente')
      .defined('Não foi possível validar o login do atendente'),
    queueIds: yup
      .array(yup.number())
      .required('Não foi possível validar as filas do atendente'),
    pauseId: yup.number().nullable(),
    name: yup.string().defined('Não foi possível validar o nome do atendente')
  })
);
